import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Login from '@/views/Login.vue';
import LegacyView from '@/views/LegacyView.vue';
import store from '@/store';
import { currentUserHasPermission } from '@/helpers';
import { initialView, isRememberableView } from '@/helpers/lastView';
import { leadIdFromRouteCode, leadRouteCode } from '@/helpers/leadCode';

const Lead = () => import('@/views/Lead.vue');
const Operations = () => import('@/views/Operations.vue');
const Connections = () => import('@/views/Connections.vue');
const Config = () => import('@/views/Config.vue');
const Rules = () => import('@/views/Rules.vue');
const RuleEditor = () => import('@/views/RuleEditor.vue');
const RulesArchive = () => import('@/views/RulesArchive.vue');
const RuleExecutions = () => import('@/views/RuleExecutions.vue');

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  {
    path: '*',
    redirect: '/login',
  },
  // Ingresso: il portale riapre sull'ultima vista usata.
  // Nessun componente e nessun `redirect`: un redirect viene risolto durante
  // il match, cioè prima delle guardie, quando l'utente non è ancora stato
  // caricato e la vista memorizzata non si può ancora leggere. Qui invece
  // `beforeEnter` gira dopo la `beforeEach` globale, che l'utente lo carica.
  {
    path: '/',
    name: 'Home',
    beforeEnter: (to, from, next) => next({
      path: initialView(store.getters.lastView),
      replace: true,
    }),
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  // Redirect per il branding
  {
    path: '/genera',
    redirect: {
      path: '/login',
      query: { brand: 'genera' },
    },
  },
  {
    path: '/energifera',
    redirect: {
      path: '/login',
      query: { brand: 'energifera' },
    },
  },
  {
    path: '/l',
    name: 'Elenco',
    component: LegacyView,
  },
  {
    path: '/l/n',
    name: 'NuovaLead',
    component: LegacyView,
  },

  // La pagina della lead si apre dal codice (`/l/syp00006`). I link spediti
  // prima portano l'id (`/l/6`): aprono la stessa lead e l'indirizzo passa al
  // codice. Vale solo entrando nella rotta, quindi i link interni devono
  // comporre il codice con `leadRouteCode`.
  {
    path: '/l/:code',
    name: 'Lead',
    component: Lead,
    beforeEnter: (to, from, next) => {
      const id = leadIdFromRouteCode(to.params.code);
      if (Number.isNaN(id) || to.params.code === leadRouteCode(id)) return next();

      return next({
        name: 'Lead',
        params: { ...to.params, code: leadRouteCode(id) },
        query: to.query,
        hash: to.hash,
        replace: true,
      });
    },
  },

  // Commesse
  {
    path: '/o',
    name: 'Operations',
    component: Operations,
  },

  // Connessioni
  {
    path: '/co',
    name: 'Connections',
    component: Connections,
  },

  // Documentazione
  {
    path: '/d',
    name: 'Documentazione',
    component: LegacyView,
  },

  // Configurazione
  {
    path: '/config',
    name: 'Configurazione',
    component: Config,
  },

  // Utenti
  {
    path: '/u',
    name: 'ElencoUtenti',
    component: LegacyView,
  },
  {
    path: '/u/n',
    name: 'NuovoUtente',
    component: LegacyView,
  },
  {
    path: '/u/:id',
    name: 'Utente',
    component: LegacyView,
  },

  // Gruppi
  {
    path: '/g',
    name: 'ElencoGruppi',
    component: LegacyView,
  },
  {
    path: '/g/n',
    name: 'NuovoGruppo',
    component: LegacyView,
  },
  {
    path: '/g/:id',
    name: 'Gruppo',
    component: LegacyView,
  },

  // Reti
  {
    path: '/rv',
    name: 'ElencoReti',
    component: LegacyView,
  },
  {
    path: '/rv/n',
    name: 'NuovaRete',
    component: LegacyView,
  },
  {
    path: '/rv/:id',
    name: 'Rete',
    component: LegacyView,
  },

  // Modelli
  {
    path: '/m',
    name: 'ElencoModelli',
    component: LegacyView,
  },
  {
    path: '/m/n',
    name: 'NuovoModello',
    component: LegacyView,
  },
  {
    path: '/m/:id',
    name: 'Modello',
    component: LegacyView,
  },

  // Categorie
  {
    path: '/c',
    name: 'ElencoCategorie',
    component: LegacyView,
  },
  {
    path: '/c/n',
    name: 'NuovaCategoria',
    component: LegacyView,
  },
  {
    path: '/c/:id',
    name: 'Categoria',
    component: LegacyView,
  },

  // Tag
  {
    path: '/t',
    name: 'ElencoTag',
    component: LegacyView,
  },
  {
    path: '/t/n',
    name: 'NuovoTag',
    component: LegacyView,
  },
  {
    path: '/t/:id',
    name: 'Tag',
    component: LegacyView,
  },

  // Regole
  {
    path: '/r',
    name: 'ElencoRegole',
    component: Rules,
  },
  {
    path: '/r/n',
    name: 'NuovaRegola',
    component: RuleEditor,
  },
  {
    path: '/r/archivio',
    name: 'ArchivioRegole',
    component: RulesArchive,
  },
  {
    path: '/r/esecuzioni',
    name: 'EsecuzioniRegole',
    component: RuleExecutions,
  },
  {
    path: '/r/:id',
    name: 'Regola',
    component: RuleEditor,
  },
  // Checklist
  {
    path: '/cl',
    name: 'ElencoChecklist',
    component: LegacyView,
  },
  {
    path: '/cl/n',
    name: 'NuovaChecklist',
    component: LegacyView,
  },
  {
    path: '/cl/:id',
    name: 'Checklist',
    component: LegacyView,
  },

  {
    path: '/me',
    name: 'Profilo',
    component: LegacyView,
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

function hasAccess(route) {
  console.log('hasAccess() route =', route); // eslint-disable-line no-console

  // Per la route /l (elenco lead) il permesso richiesto è 'leads.read'
  if (route.path === '/l') {
    return currentUserHasPermission('leads.read');
  }
  // Per la route /o (elenco commesse) il permesso richiesto è 'operations.read'
  if (route.path === '/o') {
    return currentUserHasPermission('operations.read');
  }
  // Per la route /co (elenco connessioni) il permesso richiesto è 'connections.read'
  if (route.path === '/co') {
    return currentUserHasPermission('connections.read');
  }
  // Per la route /d (documentazione) il permesso richiesto è 'docs.read'
  if (route.path === '/d') {
    return currentUserHasPermission('docs.read');
  }
  // Per le route /r* (regole): 'rules.read' per lista, 'rules.manage' per editor
  if (route.path === '/r') {
    return currentUserHasPermission('rules.read');
  }
  if (route.path === '/r/archivio') {
    return currentUserHasPermission('rules.read');
  }
  // Il log delle esecuzioni si legge con il permesso delle regole
  if (route.path === '/r/esecuzioni') {
    return currentUserHasPermission('rules.read');
  }
  if (route.path.startsWith('/r/')) {
    return currentUserHasPermission('rules.manage');
  }
  // Per la route /config (configurazione) è richiesto essere super user
  if (route.path === '/config') {
    // eslint-disable-next-line prefer-destructuring
    const currentUser = store.getters.currentUser;
    return currentUser && currentUser.isSuperUser === true;
  }

  // FIXME: Aggiungere altre route e permessi
  return true;
}

// eslint-disable-next-line consistent-return
router.beforeEach(async (to, from, next) => {
  console.log('beforeEach() to =', to); // eslint-disable-line no-console

  // Se non è richiesta l'autenticazione...
  const publicPages = ['/login'];
  const authRequired = !publicPages.includes(to.path);
  // ... passa direttamente alla pagina pubblica.
  if (!authRequired) return next();

  // eslint-disable-next-line prefer-destructuring
  let currentUser = store.getters.currentUser;
  if (!currentUser) {
    try {
      currentUser = await store.dispatch('fetchUser');
    } catch (error) {
      console.error('beforeEach() error =', error); // eslint-disable-line no-console
    }
  }

  // Se l'utente non è autenticato, passa alla pagina di login.
  if (!currentUser) {
    console.log('beforeEach() !currentUser'); // eslint-disable-line no-console

    return next({
      path: '/login',
      // HACK: Era disattivato non ricordo perché, ma ora è necessario per il redirect dopo il login.
      query: {
        redirect: to.fullPath,
      },
    });
  }

  // currentUser è definito

  console.log('beforeEach() to.path =', to.path); // eslint-disable-line no-console

  if (hasAccess(to)) {
    console.log('beforeEach() hasAccess() = true'); // eslint-disable-line no-console
    return next();
  }

  // Se il path richiesto è `/l`, `/o`, `/co` o `/d`...
  if (['/l', '/o', '/co', '/d'].includes(to.path)) {
    console.log(`beforeEach() from.path = ${from.path} and to.path = ${to.path}`); // eslint-disable-line no-console

    const routesWithAccess = {
      '/l': 'leads.read',
      '/o': 'operations.read',
      '/co': 'connections.read',
      '/d': 'docs.read',
    };

    // Se l'utente ha accesso alla route selezionata in `to.path`...
    if (currentUserHasPermission(routesWithAccess[to.path])) {
      console.log('beforeEach() currentUserHasPermission() = true'); // eslint-disable-line no-console
      // ... effettua il redirect verso la route selezionata
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return router.push({ path: to.path }).catch(() => {});
    }

    // ... effettua il redirect verso la prima view a cui l'utente ha accesso
    // eslint-disable-next-line no-restricted-syntax
    for (const [path, permission] of Object.entries(routesWithAccess)) {
      console.log('beforeEach() path =', path); // eslint-disable-line no-console
      console.log('beforeEach() permission =', permission); // eslint-disable-line no-console

      if (currentUserHasPermission(permission)) {
        console.log('beforeEach() currentUserHasPermission() = true'); // eslint-disable-line no-console
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return router.push({ path }).catch(() => {});
      }
    }
  }

  // Se l'utente è autenticato...
  return hasAccess(to)
    ? next()
    : next({
      path: '/login',
      // query: {
      //   redirect: to.fullPath,
      // },
    });
});

// Segna la vista appena raggiunta. L'azione non scrive se non è cambiata.
router.afterEach((to) => {
  // Se il salvataggio fallisce non deve diventare un errore in console a ogni
  // navigazione: è una preferenza, non un dato di lavoro.
  if (isRememberableView(to.path)) {
    store.dispatch('saveLastView', to.path).catch(() => {});
  }
});

export default router;
